/**
 * Shared image layout helpers for tool-prep previews and AR thumbnails.
 *
 * ## Tool PrepPopup canvas contract (all editing tools)
 *
 * 1. Empty popup — shared fitted stage (square aspect fallback) via
 *    `computeFittedStageSize` + `TOOL_PREP_STAGE_INSET`.
 * 2. After upload — stage aspect follows the image; fit contain inside the
 *    right pane (max width OR max height, aspect preserved).
 * 3. After Apply — visible canvas/frame size stays the same as the last
 *    pre-Apply fit. Pass `frameNaturalSize` (and `subjectNaturalSize` +
 *    `shadowBleedPx` for padded exports) into `ToolPrepFlatResultStage`.
 *    Never size the frame from a bled/padded result bitmap.
 * 4. Soft watermark — clipped inside the frame; must not affect stage measure.
 *
 * Exceptions (feature *is* a size change): Resize, Crop, Circle crop,
 * Padding, Outpaint — may change frame only when that is the tool’s output.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  clearToolPrepSubjectPlacement,
  publishToolPrepSubjectPlacement,
} from "./toolPrepSubjectPlacement";

/** Min time White/Transparent keep the round loader before revealing the cutout. */
export const TOOL_PREP_CUTOUT_REVEAL_MS = 2000;

/**
 * Fit a stage into a container while keeping aspect ratio constant.
 * Same idea as CanvasEditor.computeFittedCanvasSize.
 * Shared inset for all tool-prep previews (editing, AI, document).
 */
export const TOOL_PREP_STAGE_INSET = 0.9;

/** PDF / Word / page-number previews — same inset as other tools for visual parity. */
export const DOCUMENT_PREP_STAGE_INSET = 0.9;

export function computeFittedStageSize(
  containerWidth,
  containerHeight,
  aspectRatio,
  { inset = TOOL_PREP_STAGE_INSET } = {}
) {
  if (!containerWidth || !containerHeight || !aspectRatio || aspectRatio <= 0) {
    return null;
  }
  let width = containerWidth * inset;
  let height = width / aspectRatio;
  if (height > containerHeight * inset) {
    height = containerHeight * inset;
    width = height * aspectRatio;
  }
  return {
    width: Math.round(width),
    height: Math.round(height),
  };
}

/**
 * Fixed-width thumb size from an aspect ratio (width / height).
 */
export function thumbSizeFromAspect(aspectRatio, widthPx = 96, fallbackAspect = 1) {
  const aspect =
    aspectRatio && aspectRatio > 0 ? aspectRatio : fallbackAspect;
  const width = widthPx;
  const height = Math.max(1, Math.round(width / aspect));
  return { width, height, aspect };
}

/**
 * Where the full source image sits on the live stage, as fractions of the stage.
 * A tightened subject box is expanded back to the full image so Apply can
 * redraw the baked bitmap in the same place instead of recentering it.
 */
export function subjectPlacementFromObject(object, contentCrop, stageSize) {
  if (!object?.width || !object?.height || !stageSize?.width || !stageSize?.height) {
    return null;
  }
  let x = Number(object.x) || 0;
  let y = Number(object.y) || 0;
  let width = Number(object.width) || 0;
  let height = Number(object.height) || 0;
  if (contentCrop?.width > 0 && contentCrop?.imageWidth > 0) {
    const scaleX = width / contentCrop.width;
    const scaleY = height / contentCrop.height;
    x -= (Number(contentCrop.x) || 0) * scaleX;
    y -= (Number(contentCrop.y) || 0) * scaleY;
    width = contentCrop.imageWidth * scaleX;
    height = contentCrop.imageHeight * scaleY;
  }
  if (!(width > 0) || !(height > 0)) return null;
  const crop =
    contentCrop?.width > 0 && contentCrop?.imageWidth > 0
      ? {
          x: Number(contentCrop.x) || 0,
          y: Number(contentCrop.y) || 0,
          width: Number(contentCrop.width),
          height: Number(contentCrop.height),
          imageWidth: Number(contentCrop.imageWidth),
          imageHeight: Number(contentCrop.imageHeight),
        }
      : null;
  return {
    x: x / stageSize.width,
    y: y / stageSize.height,
    width: width / stageSize.width,
    height: height / stageSize.height,
    rotation: Number(object.rotation) || 0,
    crop,
  };
}

/**
 * Publish the live subject box for Open in Editor, and optionally mirror it
 * into a tool ref (post-Apply flat canvas).
 * A new image clears the previous tool's box so it cannot leak across tools.
 */
export function useReportSubjectPlacement(
  onSubjectPlacement,
  object,
  contentCrop,
  stageSize,
  imageUrl = null
) {
  const imageKeyRef = useRef(imageUrl);
  useEffect(() => {
    if (imageUrl !== imageKeyRef.current) {
      imageKeyRef.current = imageUrl;
      clearToolPrepSubjectPlacement();
    }
    const placement = subjectPlacementFromObject(object, contentCrop, stageSize);
    if (!placement) return;
    publishToolPrepSubjectPlacement(placement);
    if (typeof onSubjectPlacement === "function") onSubjectPlacement(placement);
  }, [onSubjectPlacement, object, contentCrop, stageSize, imageUrl]);
}

export function aspectFromSize(width, height, fallback = 1) {
  const w = Number(width);
  const h = Number(height);
  if (w > 0 && h > 0) return w / h;
  return fallback;
}

/**
 * Load naturalWidth/Height for an image URL.
 */
export function useImageNaturalSize(imageUrl) {
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!imageUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    if (
      typeof imageUrl === "string" &&
      /^https?:\/\//i.test(imageUrl) &&
      !imageUrl.startsWith("blob:") &&
      !imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      if (cancelled) return;
      setNaturalSize({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      if (!cancelled) setNaturalSize({ width: 0, height: 0 });
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return naturalSize;
}

/**
 * Measure a tool-prep preview pane via layout + window resize only.
 * Avoids ResizeObserver loops when canvas/image size changes after upload.
 */
export function useToolPrepPreviewViewport(containerRef, refreshKey) {
  const [viewport, setViewport] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const read = () => {
      const width = Math.round(el.clientWidth);
      const height = Math.round(el.clientHeight);
      setViewport((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height }
      );
    };

    read();
    const rafId = requestAnimationFrame(() => {
      read();
      requestAnimationFrame(read);
    });
    window.addEventListener("resize", read);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", read);
    };
  }, [containerRef, refreshKey]);

  return viewport;
}

/**
 * True once `imageUrl` has decoded AND the min delay (from mount) has elapsed.
 * Used so White/Transparent preview thumbs don't flash before the cutout is ready.
 */
export function useDelayedImageReady(
  imageUrl,
  minDelayMs = TOOL_PREP_CUTOUT_REVEAL_MS
) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [delayDone, setDelayDone] = useState(minDelayMs <= 0);

  useEffect(() => {
    if (minDelayMs <= 0) {
      setDelayDone(true);
      return undefined;
    }
    setDelayDone(false);
    const timer = setTimeout(() => setDelayDone(true), minDelayMs);
    return () => clearTimeout(timer);
  }, [minDelayMs]);

  useEffect(() => {
    if (!imageUrl) {
      setImageLoaded(false);
      return undefined;
    }
    let cancelled = false;
    setImageLoaded(false);
    const img = new Image();
    if (
      typeof imageUrl === "string" &&
      /^https?:\/\//i.test(imageUrl) &&
      !imageUrl.startsWith("blob:") &&
      !imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    const markReady = () => {
      if (!cancelled) setImageLoaded(true);
    };
    img.onload = markReady;
    img.onerror = markReady;
    img.src = imageUrl;
    if (img.complete && img.naturalWidth > 0) {
      markReady();
    }
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  return Boolean(imageUrl) && imageLoaded && delayDone;
}
